import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  model,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { isEqual } from 'lodash';
import {
  CardComponent,
  CardGroupComponent,
  FormsUtil,
  InputErrorsComponent,
  MbsSize,
  NumberComponent,
  SelectComponent,
  SizeConverterPipe,
  SwitcherComponent,
  WizardStep
} from 'mbs-ui-kit';
import { DestinationFolderInputComponent } from 'mbs-ui-shared@components/destination-folder-input/destination-folder-input.component';
import {
  VmNameFieldComponent,
  VmNameFieldMachine,
  VmNameFieldMode,
  VmNameFieldValue
} from 'mbs-ui-shared@components/vm-name-field/vm-name-field.component';
import { destinationToVMValidator } from 'mbs-ui-shared@constants';
import { ErrorCode } from 'mbs-ui-shared@constants/errorCodes';
import { first, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DestinationFolderParams } from 'wizards/models/destination-models';
import { RestorePartitionItem } from 'wizards/models/partitions-models';
import { SelectHostStepValue } from 'wizards/models/select-host-models';
import { VirtualMachinesType } from 'wizards/models/select-virtual-machines-models';
import { RestoreAsDiskEnum } from 'wizards/models/type-data-models';
import {
  DefaultVmSettingsStepValue,
  ProvisionModeEnum,
  VmSettingsStepValue,
  VmSettingsStepValueForm
} from 'wizards/models/vm-settings-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import {
  ParamsForGetCardModel,
  ParamsForGetDataStore,
  ParamsForGetGuestOS,
  ParamsForGetVirtualMachinesOrDisksList,
  ParamsForGetVMNetworks,
  WizardStepsService
} from 'wizards/services/wizard-steps.service';
import { StepLoadInfo } from 'wizards/steps/StepBase.class';
import { dynamicMemoryCheck, maxStartupRAM, minStartupRAM } from 'wizards/steps/archive-consistency-step/consistency-step.utils';

const CanShareVersion = 795;

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NumberComponent,
    CardComponent,
    CardGroupComponent,
    SelectComponent,
    I18NextPipe,
    ReactiveFormsModule,
    SwitcherComponent,
    DestinationFolderInputComponent,
    SizeConverterPipe,
    InputErrorsComponent,
    VmNameFieldComponent
  ],
  standalone: true,
  selector: 'mbs-vm-settings-step',
  templateUrl: './vm-settings-step.component.html'
})
export class VmSettingsStepComponent {
  i18nPipe = inject(I18NextPipe);
  mainService = inject(RemoteManagementWizardsService);
  stepService = inject(WizardStepsService);

  public restorePartitions: InputSignal<RestorePartitionItem[]> = input<RestorePartitionItem[]>([]);
  public restoreAs: InputSignal<RestoreAsDiskEnum> = input<RestoreAsDiskEnum>(RestoreAsDiskEnum.RestoreToVMwareVSphere);
  public drServerHid: InputSignal<string> = input<string>(null);

  public readonly loadInfo: OutputEmitterRef<StepLoadInfo> = output<StepLoadInfo>();
  public readonly nextStep: OutputEmitterRef<boolean> = output<boolean>();

  public readonly MbsSize = MbsSize;
  public readonly maxStartupRAM = maxStartupRAM;
  public readonly minStartupRAM = minStartupRAM;

  private readonly proxmoxTypes = [RestoreAsDiskEnum.RestoreToProxmox, RestoreAsDiskEnum.InstantRestoreToProxmox];
  private readonly hyperVTypes = [RestoreAsDiskEnum.RestoreToHyperV, RestoreAsDiskEnum.InstantRestoreToHyperV];
  private readonly paramsForDataStore: ParamsForGetDataStore = {
    agentType: 'backup',
    commandType: 'GetVirtualMachineDataStoreList',
    params: {
      PlanId: this.mainService.planId,
      Type: VirtualMachinesType.VMWare,
      Server: '',
      Login: '',
      Password: '',
      IsCluster: false,
      Offset: null,
      Limit: null,
      Order: 'DisplayNameAsc',
      Search: null,
      Host: ''
    }
  };
  private readonly paramsForMachines: ParamsForGetVirtualMachinesOrDisksList = {
    agentType: 'backup',
    commandType: 'GetVirtualMachineList',
    params: {
      PlanId: this.mainService.planId,
      Type: 'VMWare',
      Server: '',
      Login: '',
      Password: '',
      IsCluster: false,
      Order: 'DisplayNameAsc'
    }
  };
  private readonly paramsForNetworks: ParamsForGetVMNetworks = {
    agentType: 'backup',
    commandType: 'GetVMNetworks',
    params: {
      PlanId: this.mainService.planId,
      Type: VirtualMachinesType[VirtualMachinesType.VMWare] as unknown as VirtualMachinesType,
      Server: '',
      Login: '',
      Password: '',
      Host: '',
      Offset: 0,
      Limit: 300
    }
  };
  private readonly paramsForGuestOS: ParamsForGetGuestOS = {
    agentType: 'backup',
    commandType: 'GetVMGuestTypes',
    params: {
      PlanId: this.mainService.planId,
      Type: VirtualMachinesType[VirtualMachinesType.VMWare] as unknown as VirtualMachinesType,
      Server: '',
      Login: '',
      Password: '',
      Host: '',
      Offset: 0,
      Limit: 300
    }
  };
  private readonly paramsForCardModels: ParamsForGetCardModel = {
    agentType: 'backup',
    commandType: 'GetVMNetworkCards',
    params: {
      PlanId: this.mainService.planId,
      Type: VirtualMachinesType[VirtualMachinesType.VMWare] as unknown as VirtualMachinesType,
      Server: '',
      Login: '',
      Password: '',
      Host: '',
      Offset: 0,
      Limit: 300
    }
  };
  public readonly value = model<VmSettingsStepValue>(DefaultVmSettingsStepValue);

  public isFirstRestoreAsState: WritableSignal<boolean> = signal(true);
  public memorySizeIsFocused: WritableSignal<boolean> = signal(false);
  public maxMemorySizeIsFocused: WritableSignal<boolean> = signal(false);
  public minMemorySizeIsFocused: WritableSignal<boolean> = signal(false);

  public cardModelsLoading: WritableSignal<boolean> = signal(false);
  public dataStoreLoading: WritableSignal<boolean> = signal(false);
  public guestOsLoading: WritableSignal<boolean> = signal(false);
  public networksLoading: WritableSignal<boolean> = signal(false);
  public virtualMachinesLoading: WritableSignal<boolean> = signal(false);

  public virtualMachines: WritableSignal<VmNameFieldMachine[]> = signal([]);
  public virtualMachinesProxmox: WritableSignal<VmNameFieldMachine[]> = signal([]);
  public vmNameFieldValid: WritableSignal<boolean> = signal(true);
  public cardModelsList: WritableSignal<{ label: string; value: number }[]> = signal([]);
  public dataStoreList: WritableSignal<{ freeSpace: number; name: string }[]> = signal([]);
  public diskProvisionList: WritableSignal<{ label: string; value: ProvisionModeEnum }[]> = signal([
    { label: this.i18nPipe.transform('wizards:vmLazyProvision'), value: ProvisionModeEnum.lazy },
    { label: this.i18nPipe.transform('wizards:vmEagerProvision'), value: ProvisionModeEnum.eager },
    { label: this.i18nPipe.transform('wizards:vmThinProvision'), value: ProvisionModeEnum.thin }
  ]);
  public virtualNetworkList: WritableSignal<string[]> = signal([]);
  public generationList: WritableSignal<{ label: string; value: boolean }[]> = signal([
    { label: this.i18nPipe.transform('wizards:vmGeneration1'), value: false },
    { label: this.i18nPipe.transform('wizards:vmGeneration2'), value: true }
  ]);
  public guestOsList: WritableSignal<{ label: string; value: string }[]> = signal([]);

  public destinationFolderParams: WritableSignal<DestinationFolderParams> = signal(null);

  public isInstantHyperV: Signal<boolean> = computed(() => this.restoreAs() === RestoreAsDiskEnum.InstantRestoreToHyperV);
  public isInstantProxmox: Signal<boolean> = computed(() => this.restoreAs() === RestoreAsDiskEnum.InstantRestoreToProxmox);
  public isInstantDRServer: Signal<boolean> = computed(() => this.restoreAs() === RestoreAsDiskEnum.InstantRestoreToDRServer);
  public isInstantRestore: Signal<boolean> = computed(() => this.isInstantHyperV() || this.isInstantProxmox() || this.isInstantDRServer());
  public isVmWare: Signal<boolean> = computed(() => this.restoreAs() === RestoreAsDiskEnum.RestoreToVMwareVSphere);
  public isProxmox: Signal<boolean> = computed(() => this.proxmoxTypes.includes(this.restoreAs()));
  public isHyperV: Signal<boolean> = computed(() => this.hyperVTypes.includes(this.restoreAs()));
  public isVmWareOrProxmox: Signal<boolean> = computed(() => this.isVmWare() || this.isProxmox());
  // The DR Server mode mirrors Hyper-V apart from the destination folder and the dynamic memory block
  public showDestinationFolder: Signal<boolean> = computed(() => !this.isVmWareOrProxmox() && !this.isInstantDRServer());
  public showDynamicMemory: Signal<boolean> = computed(() => !this.isVmWareOrProxmox() && !this.isInstantDRServer());
  public vmNameFieldMachines: Signal<ReadonlyArray<VmNameFieldMachine>> = computed(() =>
    this.isProxmox() ? this.virtualMachinesProxmox() : this.virtualMachines()
  );
  public vmNameFieldMode: Signal<VmNameFieldMode> = computed(() => (this.isProxmox() ? 'proxmox' : 'hyperV'));
  public vmNameFieldValue: Signal<VmNameFieldValue> = computed(() =>
    this.isProxmox()
      ? {
          kind: 'proxmox',
          id: this.value()?.vmId || '',
          name: this.value()?.vmName || '',
          computerName: this.mainService.compVmName || this.mainService.compName
        }
      : {
          kind: 'hyperV',
          name: this.value()?.vmName || '',
          computerName: this.mainService.compVmName || this.mainService.compName
        }
  );
  public isSomeLoading: Signal<boolean> = computed(
    () =>
      this.virtualMachinesLoading() ||
      this.networksLoading() ||
      this.dataStoreLoading() ||
      this.guestOsLoading() ||
      this.cardModelsLoading()
  );

  public stepForm = new FormGroup<VmSettingsStepValueForm>({
    vmName: new FormControl('', [Validators.required, this.vmNameNeedOverwriteValidator.bind(this)]),
    vmId: new FormControl('', [this.customNotProxmoxRequired.bind(this)]),
    numCPU: new FormControl(2, [Validators.min(1), Validators.max(100)]),
    memorySize: new FormControl(4096, [Validators.min(minStartupRAM), Validators.max(maxStartupRAM)]),
    datastore: new FormControl('', [this.customVMWareProxmoxRequired.bind(this)]),
    diskProvision: new FormControl(ProvisionModeEnum.thin),
    virtualNetwork: new FormControl('', [this.customProxmoxRequired.bind(this)]),
    cardModel: new FormControl(0, [this.cardModelMinValidator.bind(this)]),
    guestOs: new FormControl('', [this.customVMWareProxmoxRequired.bind(this)]),
    powerOn: new FormControl(false),

    destination: new FormControl(''),
    dynamic: new FormControl(false),
    generation: new FormControl(false),
    overwrite: new FormControl(false),
    maxMemorySize: new FormControl(4096, [this.maxMemorySizeValidator.bind(this), this.maxMinDynamicMemorySizeValidator.bind(this)]),
    minMemorySize: new FormControl(minStartupRAM, [
      this.minMemorySizeValidator.bind(this),
      this.maxMinDynamicMemorySizeValidator.bind(this)
    ])
  });

  private previousHostsValue: Partial<SelectHostStepValue>;

  constructor() {
    this.initSubscribes();
    this.destinationFolderParams.set(this.getParamsForDestinationFolder());
  }

  public forceValid(step: WizardStep & { isSave?: boolean; isNext?: boolean } = null): void {
    this.stepForm.markAsTouched();
    FormsUtil.triggerValidation(this.stepForm);
  }

  public vmNameFieldChangeHandler(value: VmNameFieldValue): void {
    this.stepForm.get('vmName').setValue(value.name);
    this.stepForm.get('vmId').setValue(value.kind === 'proxmox' ? value.id : '');
  }

  public overwriteChangeHandler(value: boolean): void {
    this.stepForm.get('overwrite').setValue(value);
  }

  public vmNameFieldValidityChangeHandler(valid: boolean): void {
    this.vmNameFieldValid.set(valid);
    this.stepForm.controls.vmName.updateValueAndValidity({ emitEvent: false });
    this.value.update((v) => ({ ...v, valid: this.isStepValid() }));
  }

  public vmNameEditValidationError(value: string): string | null {
    if (!value) return null;

    return destinationToVMValidator.test(value) ? this.i18nPipe.transform('wizards:errorPathForVM') : null;
  }

  public memorySizeBlurHandler(): void {
    this.memorySizeIsFocused.set(false);

    const value = this.stepForm?.get('memorySize')?.value;

    if (value < minStartupRAM) this.stepForm?.get('memorySize').setValue(minStartupRAM);
    if (value > maxStartupRAM) this.stepForm?.get('memorySize').setValue(maxStartupRAM);
  }

  public maxMemorySizeBlurHandler(): void {
    this.maxMemorySizeIsFocused.set(false);

    const value = this.stepForm?.get('maxMemorySize')?.value;

    if (value < minStartupRAM) this.stepForm?.get('maxMemorySize').setValue(minStartupRAM);
    if (value > maxStartupRAM) this.stepForm?.get('maxMemorySize').setValue(maxStartupRAM);
  }

  public minMemorySizeBlurHandler(): void {
    this.minMemorySizeIsFocused.set(false);

    const value = this.stepForm?.get('minMemorySize')?.value;

    if (value < minStartupRAM) this.stepForm?.get('minMemorySize').setValue(minStartupRAM);
    if (value > maxStartupRAM) this.stepForm?.get('minMemorySize').setValue(maxStartupRAM);
  }

  // PRIVATE SECTION START
  private initSubscribes(): void {
    effect(() => this.value.update((v) => ({ ...v, valid: this.isStepValid() })));
    effect(() => this.loadForDRServer(this.drServerHid()));
    effect(() => {
      const shouldValidateDestination = !this.isVmWareOrProxmox() && !this.isInstantRestore();
      const destinationControl = this.stepForm.controls.destination;

      if (!shouldValidateDestination) {
        destinationControl.clearValidators();
        destinationControl.setErrors(null);
      }

      destinationControl.updateValueAndValidity({ emitEvent: false });
    });
    effect(() => {
      const val = this.value();

      if (!val) return;

      this.stepForm.patchValue({ ...val }, { emitEvent: false });
    });

    this.stepForm.valueChanges.pipe(untilDestroyed(this)).subscribe((formValue) => {
      this.value.update((v) => ({ ...v, ...formValue, valid: this.isStepValid() }));
    });

    let hostSubscribe$: Subscription;

    toObservable(this.restoreAs)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          if (this.isFirstRestoreAsState()) this.isFirstRestoreAsState.set(false);
          else this.stepForm.reset(DefaultVmSettingsStepValue);

          if (this.isHyperV()) {
            this.getVirtualMachines(null);
            this.getVirtualNetworks(null);
          } else if (this.isInstantDRServer()) {
            // No host step for a local DR Server, but the lists belong to the chosen server - loadForDRServer waits for it
            hostSubscribe$?.unsubscribe();
          } else {
            hostSubscribe$?.unsubscribe();
            hostSubscribe$ = this.getSubscribeToHost();
          }
        }
      });
  }

  private loadForDRServer(hid: string): void {
    if (!this.isInstantDRServer() || !hid) return;

    this.getVirtualMachines(null);
    this.getVirtualNetworks(null);
  }

  private getSubscribeToHost(): Subscription {
    return this.stepService
      .pipeProperty('hosts')
      .pipe(
        filter((res: Partial<SelectHostStepValue>) => this.canGetData(res)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (res: Partial<SelectHostStepValue>) => {
          this.getVirtualMachines(res);
          this.getDataStore(res);
          this.getVirtualNetworks(res);
          this.getGuestOs(res);

          if (this.isProxmox()) this.getCardModels(res);
        }
      });
  }

  private getVirtualMachines(result: Partial<SelectHostStepValue>): void {
    this.virtualMachinesLoading.set(true);

    const hid = this.mainService.targetComputerData?.hid || this.mainService.hid;

    this.stepService
      .getRemoteCommandData(this.getParamsForMachinesRequest(result), hid)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (this.isProxmox()) {
            this.virtualMachines.set([]);
            this.virtualMachinesProxmox.set(res?.data?.items?.map((el) => ({ id: el.id, name: el.name })) || []);
          } else {
            this.virtualMachines.set(res?.data?.items?.map((el) => ({ name: el.name })) || []);
            this.virtualMachinesProxmox.set([]);
          }

          this.virtualMachinesLoading.set(false);
        },
        error: (e) => {
          if (this.isHyperV() && e?.error?.errorCode === ErrorCode.HyperVNotFound) {
            this.stepService.hyperVNotFoundError$.next({ title: e.error?.title, content: e.error?.detail });
          }

          this.virtualMachines.set([]);
          this.virtualMachinesProxmox.set([]);
          this.virtualMachinesLoading.set(false);
        }
      });
  }

  private getDataStore(result: Partial<SelectHostStepValue>): void {
    this.dataStoreLoading.set(true);

    const hid = this.mainService.targetComputerData?.hid || this.mainService.hid;

    this.stepService
      .getRemoteCommandData(this.getParamsForDataStoreRequest(result), hid)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (res: { data: { items: { name: string; freeSpace: number }[] } }) => {
          const list = res?.data?.items || [];
          const current = this.value()?.datastore;
          const found = list.find((ds) => ds.name === current);

          this.dataStoreList.set(list);

          if (!found && list?.length) this.stepForm.get('datastore').setValue(list[0].name);

          this.dataStoreLoading.set(false);
        },
        error: () => {
          this.dataStoreList.set([]);
          this.dataStoreLoading.set(false);
        }
      });
  }

  private getVirtualNetworks(result: Partial<SelectHostStepValue>): void {
    this.networksLoading.set(true);

    const hid = this.mainService.targetComputerData?.hid || this.mainService.hid;

    this.stepService
      .getRemoteCommandData(this.getParamsForNetwork(result), hid)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (res: { data: { items: { name: string }[] } }) => {
          const network = this.value()?.virtualNetwork;
          const list = res?.data?.items?.map((h) => h.name) || [];
          const found = list.find((n) => n === network);

          this.virtualNetworkList.set(list);
          this.networksLoading.set(false);

          if (!found && list?.length) this.stepForm.get('virtualNetwork').setValue(list[0]);
        },
        error: () => {
          this.virtualNetworkList.set([]);
          this.networksLoading.set(false);
        }
      });
  }

  private getGuestOs(result: Partial<SelectHostStepValue>): void {
    this.guestOsLoading.set(true);

    const hid = this.mainService.targetComputerData?.hid || this.mainService.hid;

    this.stepService
      .getRemoteCommandData(this.getParamsForGuestOS(result), hid)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (res: { data: { items: { name: string; id: string }[] } }) => {
          this.guestOsList.set(res?.data?.items?.map((h) => ({ label: h.name, value: h.id })) || []);
          this.guestOsLoading.set(false);
        },
        error: () => {
          this.guestOsList.set([]);
          this.guestOsLoading.set(false);
        }
      });
  }

  private getCardModels(result: Partial<SelectHostStepValue>): void {
    this.cardModelsLoading.set(true);

    const hid = this.mainService.targetComputerData?.hid || this.mainService.hid;

    this.stepService
      .getRemoteCommandData(this.getParamsForCardModels(result), hid)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (res: { data: { items: { name: string; model: number }[] } }) => {
          const cardModel = this.value()?.cardModel;
          const list = res?.data?.items?.map((h) => ({ label: h.name, value: h.model })) || [];
          const found = list.find((n) => n.value === cardModel);

          this.cardModelsList.set(list);
          this.cardModelsLoading.set(false);

          if (!found) this.stepForm.get('cardModel').setValue(0);
        },
        error: () => {
          this.cardModelsList.set([]);
          this.cardModelsLoading.set(false);
        }
      });
  }

  private canGetData(result: Partial<SelectHostStepValue>): boolean {
    const allFields = result?.host && result.server && result.login && result.password;

    if (allFields && (!this.previousHostsValue || !isEqual(this.previousHostsValue, result))) {
      this.previousHostsValue = result;
      return true;
    }

    return false;
  }

  private getTypeForParams(): VirtualMachinesType {
    if (this.isVmWare()) return VirtualMachinesType.VMWare;

    if (this.isProxmox()) return VirtualMachinesType.Proxmox;

    return this.isInstantDRServer() ? VirtualMachinesType.LDR : VirtualMachinesType.HyperV;
  }

  private getParamsForMachinesRequest(result: Partial<SelectHostStepValue>): ParamsForGetVirtualMachinesOrDisksList {
    this.paramsForMachines.params.Type = VirtualMachinesType[this.getTypeForParams()] as string;

    if (result) {
      this.paramsForMachines.params.Password = result.password;
      this.paramsForMachines.params.Login = result.login;
      this.paramsForMachines.params.Server = result.server;
    }

    return this.paramsForMachines;
  }

  private getParamsForGuestOS(result: Partial<SelectHostStepValue>): ParamsForGetGuestOS {
    this.paramsForGuestOS.params.Type = VirtualMachinesType[this.getTypeForParams()] as string;

    if (result) {
      this.paramsForGuestOS.params.Password = result.password;
      this.paramsForGuestOS.params.Login = result.login;
      this.paramsForGuestOS.params.Server = result.server;
      this.paramsForGuestOS.params.Host = result.host;
    }

    return this.paramsForGuestOS;
  }

  private getParamsForNetwork(result: Partial<SelectHostStepValue>): ParamsForGetVMNetworks {
    this.paramsForNetworks.params.Type = VirtualMachinesType[this.getTypeForParams()] as string;

    if (result) {
      this.paramsForNetworks.params.Password = result.password;
      this.paramsForNetworks.params.Login = result.login;
      this.paramsForNetworks.params.Server = result.server;
      this.paramsForNetworks.params.Host = result.host;
    }

    return this.paramsForNetworks;
  }

  private getParamsForDataStoreRequest(result: Partial<SelectHostStepValue>): ParamsForGetDataStore {
    this.paramsForDataStore.params.Type = VirtualMachinesType[this.getTypeForParams()] as string;
    this.paramsForDataStore.params.Password = result.password;
    this.paramsForDataStore.params.Login = result.login;
    this.paramsForDataStore.params.Server = result.server;
    this.paramsForDataStore.params.Host = result.host;

    return this.paramsForDataStore;
  }

  private getParamsForCardModels(result: Partial<SelectHostStepValue>): ParamsForGetCardModel {
    this.paramsForCardModels.params.Type = VirtualMachinesType[this.getTypeForParams()] as string;

    if (result) {
      this.paramsForCardModels.params.Password = result.password;
      this.paramsForCardModels.params.Login = result.login;
      this.paramsForCardModels.params.Server = result.server;
      this.paramsForCardModels.params.Host = result.host;
    }

    return this.paramsForCardModels;
  }

  private getParamsForDestinationFolder(): DestinationFolderParams {
    const toDiff = !!this.mainService?.targetComputerData;

    return {
      canUseShareTree: this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, 3) >= CanShareVersion,
      dataForPath: {
        isUnix: false,
        regExpAfter: /\\[ ]/,
        regExpBefore: /[ ]\\/,
        split: '\\ ',
        join: '\\',
        splitForBefore: ' \\'
      },
      hid: this.mainService?.targetComputerData?.hid || this.mainService.hid,
      toDifferent: toDiff,
      isOffline: this.mainService?.isOffline
    };
  }

  private isStepValid(): boolean {
    return !this.isSomeLoading() && this.stepForm.valid && this.vmNameFieldValid();
  }

  private vmNameNeedOverwriteValidator(control: AbstractControl): ValidationErrors | null {
    return this.vmNameFieldValid() ? null : { needOverwrite: true };
  }

  private cardModelMinValidator(control: AbstractControl): ValidationErrors | null {
    if (!this.isProxmox()) return null;

    return Validators.nullValidator(control);
  }

  private customProxmoxRequired(control: AbstractControl): ValidationErrors | null {
    if (this.isProxmox() || this.isInstantRestore()) return null;

    return Validators.required(control);
  }

  private customNotProxmoxRequired(control: AbstractControl): ValidationErrors | null {
    if (!this.isProxmox()) return null;

    return Validators.required(control);
  }

  private customVMWareProxmoxRequired(control: AbstractControl): ValidationErrors | null {
    if (!this.isVmWareOrProxmox() || this.isInstantRestore()) return null;

    return Validators.required(control);
  }

  private maxMinDynamicMemorySizeValidator(control: AbstractControl): ValidationErrors | null {
    if (!this.isHyperV()) return null;

    const value = this.value();

    if (!value?.dynamic) return null;

    const min = value.minMemorySize;
    const max = value.maxMemorySize;
    const startupRAM = value.memorySize;
    const validationResult = dynamicMemoryCheck(min || 0, max || 0, startupRAM);

    return validationResult
      ? { minMaxMemoryError: { message: this.i18nPipe.transform('wizards:consistencyCheck.dynamicMemoryErrorMessage', validationResult) } }
      : null;
  }

  private maxMemorySizeValidator(control: AbstractControl): ValidationErrors | null {
    if (!this.isHyperV()) return null;

    const value = this.value();

    if (!value?.dynamic) return null;

    return (control?.value || 0) < (value?.minMemorySize || 0) ? { maxMemorySizeInvalid: true } : null;
  }

  private minMemorySizeValidator(control: AbstractControl): ValidationErrors | null {
    if (!this.isHyperV()) return null;

    const value = this.value();

    if (!value?.dynamic) return null;

    return (control?.value || 0) > (value?.maxMemorySize || 0) ? { minMemorySizeInvalid: true } : null;
  }
}
